import * as React from "react";
import { Button, Card, Modal, Tabs } from "antd";
import { LoggingProvider, OrgInput } from "../../../schema/types/org/input";
import { v4 as uuidv4 } from "uuid";
import { PlusOutlined } from "@ant-design/icons";
import { ExternalLogs } from "./externalLogs";
import { getDefaultLoggingInput, loggingProviderOptions } from "../../../schema/types/org/utils";
import { CardTitle } from "../../../components/antd/card/CardTitle";
import { CardContent } from "../../../components/antd/card/CardContent";
import { getNameOfProvider } from "./utils";
import { formSetValue, formTriggerValidation, useCpFormValue } from "../../gvc/testNewForm/utils";
import { store } from "../../../store";
import { cloneDeep } from "lodash";
import { useCpFormContext } from "../../../components/form/useCpFormContext";
import { TabLabel } from "../../workload/draft/tabLabel";

interface Props {}
export const ExternalLogsTabs: React.FC<Props> = ({}) => {
  const { id: formId } = useCpFormContext();

  const logging = useCpFormValue(`logging`);
  const activeKeyExternalLog = useCpFormValue(`meta.activeKeyExternalLog`);

  const onChange = (newActiveKey: string) => {
    formSetValue(formId, `meta.activeKeyExternalLog`, newActiveKey);
  };

  const add = () => {
    const form = store.getState().form.instances[formId];
    const values: OrgInput = form.values as any;

    const _logging = cloneDeep(values.logging);

    const id = uuidv4();

    let unusedProvider: LoggingProvider = "cloudWatch";
    const usedProviders = _logging.map((el) => el.provider);

    for (const option of loggingProviderOptions) {
      if (!usedProviders.includes(option.value)) {
        unusedProvider = option.value;
        break;
      }
    }

    _logging.push({
      ...getDefaultLoggingInput(unusedProvider),
      id: id,
      provider: unusedProvider,
    });

    formSetValue(formId, `logging`, _logging);
    formSetValue(formId, `meta.activeKeyExternalLog`, `external-logs-${id}`);

    formTriggerValidation(formId);
  };

  const tryRemove = (targetKeyStr: React.MouseEvent | React.KeyboardEvent | string) => {
    Modal.confirm({
      title: "Remove External Log",
      content: "You will lose the configuration you set up for this provider, are you sure that you want to remove it?",
      onOk: () => remove(targetKeyStr),
    });
  };

  const remove = (targetKey: React.MouseEvent | React.KeyboardEvent | string) => {
    const form = store.getState().form.instances[formId];
    const values: OrgInput = form.values as any;

    let _logging = cloneDeep(values.logging);

    let newActiveKey = `external-logs-${_logging[0].id!}`;
    for (const log of _logging) {
      const key = `external-logs-${log.id}`;
      if (targetKey === key) {
        continue;
      }
      newActiveKey = key;
      break;
    }

    const targetExternalLogsId = (targetKey as string).split(`external-logs-`)[1];

    _logging = _logging.filter((lo) => lo.id !== targetExternalLogsId);

    formSetValue(formId, `meta.activeKeyExternalLog`, newActiveKey);
    formSetValue(formId, `logging`, _logging);

    formTriggerValidation(formId);
  };

  const onEdit = (targetKey: React.MouseEvent | React.KeyboardEvent | string, action: "add" | "remove") => {
    if (action === "add") {
      add();
    } else {
      tryRemove(targetKey);
    }
  };

  return (
    <>
      <div className="cpln-form-section cpln-ant-card-container cpln-ant-tabs-container">
        {logging.length > 0 ? (
          <Tabs
            type={"editable-card"}
            items={logging.map((log, index) => {
              const label = log.provider ? getNameOfProvider(log.provider) : "Select Provider";

              return {
                key: `external-logs-${log.id}`,
                label: <TabLabel label={label} names={[`logging.${index}`]} showDirty />,
                children: <ExternalLogs index={index} />,
              };
            })}
            onChange={onChange}
            activeKey={activeKeyExternalLog}
            onEdit={onEdit}
            hideAdd={logging.length >= 4}
            addIcon={
              <div className="flex items-center gap-2 px-4">
                <div>Add {logging.length < 1 ? "External Log Provider" : ""}</div>
                <PlusOutlined />
              </div>
            }
          />
        ) : (
          <Card>
            <CardTitle>External Logs</CardTitle>
            <CardContent>
              <div className="mb-2">There is no external provider added</div>
              <Button onClick={() => onEdit("", "add")}>Add External Log Provider</Button>
            </CardContent>
          </Card>
        )}
      </div>
    </>
  );
};
